'use client'

import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'

import ReadinessResults from './components/ReadinessResults'
import TeaserResult, {
  type TeaserReport,
} from '@/components/tools/TeaserResult'
import { useToolUsage } from '@/lib/use-tool-usage'
import { useToolAccessGate } from '@/components/tools/ToolAccessGate'
import { UsageLimitGate } from '@/components/tools/UsageLimitGate'

import ToolAiReadinessScoreRef from '@/components/reference/ToolAiReadinessScoreRef'
import { useUrlRun } from '@/components/reference/tool-run'
import '@/components/reference/tool-page.css'
import '@/components/reference/tool-live.css'
import { useScrollIntoView } from '@/hooks/useScrollIntoView'

// Matches API response shape
interface CategoryResult {
  score: number
  maxScore: number
  color: string
  findings: { text: string; status: 'good' | 'warning' | 'bad' }[]
}

interface AiReadinessResult {
  url: string
  domain: string
  checkedAt: string
  overallScore: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
  categories: {
    botDiscoverability: CategoryResult
    structuredData: CategoryResult
    llmCommunication: CategoryResult
    contentAccessibility: CategoryResult
    crossSignalReadiness: CategoryResult
  }
  crawlCheckAvailable: boolean
  llmsValidatorAvailable: boolean
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
}

const LOADING_STEPS = [
  'Starting AI readiness scan...',
  'Checking bot access and robots.txt...',
  'Analyzing structured data...',
  'Validating llms.txt...',
  'Analyzing entity definitions...',
  'Computing readiness scores...',
  'Generating AI recommendations...',
]

const SCORE_CATEGORIES = [
  { label: 'Bot Discoverability', color: '#008dcc', pts: '30 pts' },
  { label: 'Structured Data', color: '#8b5cf6', pts: '25 pts' },
  { label: 'LLM Communication', color: '#FDC304', pts: '25 pts' },
  { label: 'Content Accessibility', color: '#22c55e', pts: '15 pts' },
  { label: 'Cross-Signal Readiness', color: '#F90B8A', pts: '5 pts' },
]

function ResultsSkeleton() {
  const [stepIndex, setStepIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setStepIndex(prev => (prev < LOADING_STEPS.length - 1 ? prev + 1 : prev))
    }, 2500)
    return () => clearInterval(interval)
  }, [])

  const progress = Math.round(((stepIndex + 1) / LOADING_STEPS.length) * 100)

  return (
    <div className='space-y-6'>
      {/* Progress indicator */}
      <div className='bg-muted/50 rounded-2xl border border-line-strong p-6'>
        <div className='flex flex-col items-center gap-5 py-4'>
          {/* Animated score ring */}
          <div className='relative w-36 h-36'>
            <svg viewBox='0 0 120 120' className='w-full h-full -rotate-90'>
              <circle
                cx='60'
                cy='60'
                r='52'
                fill='none'
                stroke='var(--border)'
                strokeWidth='8'
                opacity={0.3}
              />
              <circle
                cx='60'
                cy='60'
                r='52'
                fill='none'
                stroke='var(--primary)'
                strokeWidth='8'
                strokeLinecap='round'
                strokeDasharray={`${2 * Math.PI * 52}`}
                strokeDashoffset={`${2 * Math.PI * 52 * (1 - progress / 100)}`}
                opacity={0.4}
                className='transition-all duration-1000 ease-out'
              />
            </svg>
            <div className='absolute inset-0 flex flex-col items-center justify-center'>
              <Loader2
                size={24}
                className='animate-spin text-muted-foreground mb-1'
              />
              <span className='text-xs font-medium text-muted-foreground'>
                {progress}%
              </span>
            </div>
          </div>

          {/* Step indicator */}
          <div className='text-center'>
            <p className='text-sm font-medium text-foreground mb-3'>
              {LOADING_STEPS[stepIndex]}
            </p>
            <div className='flex items-center justify-center gap-1.5'>
              {LOADING_STEPS.map((_, i) => (
                <div
                  key={i}
                  className='h-1.5 rounded-full transition-all duration-500'
                  style={{
                    width: i === stepIndex ? '24px' : '6px',
                    backgroundColor:
                      i <= stepIndex ? 'var(--primary)' : 'var(--border)',
                    opacity: i <= stepIndex ? 1 : 0.3,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Score cards skeleton */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {SCORE_CATEGORIES.map((cat, i) => {
          const stepMap = [1, 2, 3, 4, 5]
          const active = stepIndex >= stepMap[i]
          return (
            <div
              key={cat.label}
              className='bg-muted/50 rounded-2xl border border-line-strong p-5'
              style={{
                opacity: active ? 1 : 0.3,
                transition: 'opacity 0.5s ease',
              }}
            >
              <div className='flex items-center justify-between mb-3'>
                <div className='flex items-center gap-2'>
                  <span
                    className='h-2 w-2 rounded-full'
                    style={{ backgroundColor: cat.color }}
                  />
                  <span className='text-sm font-semibold text-foreground/70'>
                    {cat.label}
                  </span>
                </div>
                <span
                  className='text-[10px] font-bold rounded-full px-2 py-0.5'
                  style={{
                    backgroundColor: `${cat.color}15`,
                    color: cat.color,
                    opacity: active ? 1 : 0.3,
                  }}
                >
                  {cat.pts}
                </span>
              </div>
              <div className='skeleton h-2 w-full rounded-full mb-3' />
              <div className='space-y-1.5'>
                <div
                  className='skeleton h-3 rounded'
                  style={{ width: '70%' }}
                />
                <div
                  className='skeleton h-3 rounded'
                  style={{ width: '50%' }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function AiReadinessScorePage() {
  const scrollIntoView = useScrollIntoView()
  const [result, setResult] = useState<AiReadinessResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const toolUsage = useToolUsage('ai-readiness')
  const gate = useToolAccessGate('ai-readiness', 'AI Readiness Score', {
    variant: 'reference',
  })

  function validateUrl(value: string): boolean {
    if (!value.trim()) return false
    let test = value.trim()
    if (!test.startsWith('http://') && !test.startsWith('https://')) {
      test = `https://${test}`
    }
    try {
      const parsed = new URL(test)
      return parsed.hostname.includes('.')
    } catch {
      return false
    }
  }

  async function handleSubmit(inputUrl: string) {
    const trimmed = inputUrl.trim()
    if (!trimmed) {
      setError('Please enter a URL.')
      return
    }
    if (!validateUrl(trimmed)) {
      setError('Please enter a valid URL (e.g. example.com).')
      return
    }

    if (!gate.ready) {
      // Resumes after the code verifies, so the typed URL runs (PIX-47).
      gate.openGate(trimmed, () => void runCheck(trimmed))
      return
    }

    await runCheck(trimmed)
  }

  async function runCheck(trimmed: string) {
    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await toolUsage.fetchWithUsage(
        '/api/tools/ai-readiness',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: trimmed }),
        }
      )

      const data = await response.json()

      if (
        response.status === 403 &&
        (data.error === 'usage_limit' || data.error === 'gate_required')
      ) {
        gate.openGate(trimmed, () => void runCheck(trimmed))
        return
      }

      if (response.status === 429 && data.error === 'domain_cap') {
        gate.showDomainCap(data)
        return
      }

      if (!response.ok) {
        throw new Error(data.message || 'Analysis failed. Please try again.')
      }

      setResult(data as AiReadinessResult)
      gate.markConsumed()

      // Scroll to results
      scrollIntoView('readiness-results')
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const run = useUrlRun({
    example: 'pixelmojo.io',
    isLoading: isLoading,
    error: error,
    onSubmit: handleSubmit,
  })

  return (
    <div className='shell pg-tool pg-tool-ai-readiness-score'>
      {gate.modal}
      <ToolAiReadinessScoreRef
        run={run}
        output={
          <>
            <div id='readiness-results' className='max-w-4xl mx-auto'>
              {isLoading && <ResultsSkeleton />}
              {toolUsage.isGated && !isLoading && (
                <UsageLimitGate
                  variant='reference'
                  toolName='AI Readiness Score'
                  used={toolUsage.used}
                  limit={toolUsage.limit}
                />
              )}
              {result && !isLoading && !toolUsage.isGated && (
                <>
                  {(result as unknown as { access?: { tier?: string } }).access
                    ?.tier === 'teaser' ? (
                    <TeaserResult
                      variant='reference'
                      report={
                        (result as unknown as { report: TeaserReport }).report
                      }
                      toolName='AI Readiness Score'
                    />
                  ) : (
                    <ReadinessResults result={result} />
                  )}
                </>
              )}
            </div>
          </>
        }
      />
    </div>
  )
}
